/**
 * Simplified workout service for hobby fitness app
 * Focuses only on data fetching and validation, with processing delegated to DataProcessor
 */

// Simple fetch function to replace fetcher
const fetcher = async <T>(url: string, options?: RequestInit): Promise<T> => {
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers
      }
    })

    if (!response.ok) {
      const error = new Error(`HTTP ${response.status}: ${response.statusText}`)
      throw error
    }

    return response.json()
  } catch (error) {
    // Suppress console logging for 404s by re-throwing silently
    throw error
  }
}

import type {
  CategorizedOneRepMax,
  Workout,
  WeeklyStats,
  ExtendedCalendarDay
} from '../types'
import { validateWorkout } from '../utils/validation'
import { DataProcessor } from './dataProcessor'

export class WorkoutService {
  /**
   * Fetch workouts from API with basic pagination
   */
  static async fetchWorkouts(
    apiKey: string,
    page = 1
  ): Promise<{ workouts: Workout[]; hasMore: boolean }> {
    try {
      const response = await fetcher<{
        workouts: unknown[]
        page: number
        page_count: number
      }>(`https://api.hevyapp.com/v1/workouts?page=${page}`, {
        headers: {
          'api-key': apiKey
        }
      })

      const workouts = (response.workouts || [])
        .filter((workout: unknown) => validateWorkout(workout))
        .map((workout: Record<string, unknown>) => workout as Workout)

      return {
        workouts,
        hasMore: response.page < response.page_count
      }
    } catch (error) {
      // Don't log 404 errors as they're expected when there are no more pages
      if (error instanceof Error && !error.message.includes('404')) {
        console.error('Failed to fetch workouts:', error)
      }
      return { workouts: [], hasMore: false }
    }
  }

  /**
   * Fetch all workouts using page_count from initial fetch
   */
  static async fetchAllWorkouts(apiKey: string): Promise<Workout[]> {
    // First, fetch page 1 to get page_count
    const firstPageResult = await this.fetchWorkouts(apiKey, 1)
    const allWorkouts = [...firstPageResult.workouts]

    // If there are more pages, fetch them all at once
    if (firstPageResult.hasMore) {
      // Get the total page count by fetching the response directly
      const response = await fetcher<{
        workouts: unknown[]
        page: number
        page_count: number
      }>(`https://api.hevyapp.com/v1/workouts?page=1`, {
        headers: { 'api-key': apiKey }
      })

      const totalPages = response.page_count

      if (totalPages > 1) {
        // Create array of remaining page numbers [2, 3, 4, ..., totalPages]
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2)
        
        // Fetch all remaining pages in parallel
        const pagePromises = remainingPages.map(page => 
          this.fetchWorkouts(apiKey, page).catch(() => ({ workouts: [], hasMore: false }))
        )

        const results = await Promise.allSettled(pagePromises)

        // Collect workouts from successful fetches
        results.forEach((result) => {
          if (result.status === 'fulfilled' && result.value.workouts.length > 0) {
            allWorkouts.push(...result.value.workouts)
          }
        })
      }
    }

    return allWorkouts
  }

  /**
   * Calculate basic progress between workouts
   */
  static calculateProgress(
    current: Record<string, unknown>,
    previous: Record<string, unknown>
  ): number {
    const currentWeight = current?.weight as number
    const previousWeight = previous?.weight as number

    if (!currentWeight || !previousWeight) return 0

    return ((currentWeight - previousWeight) / previousWeight) * 100
  }

  /**
   * Process workout data from CSV workouts array
   */
  static async fetchWorkoutDataFromCSV(workouts: Workout[]): Promise<{
    calendarData: ExtendedCalendarDay[]
    allExercises: CategorizedOneRepMax[]
    oneRepMaxes: CategorizedOneRepMax[]
    weeklyStats: WeeklyStats
    workouts: Workout[]
  }> {
    return DataProcessor.processWorkoutData(workouts)
  }

  /**
   * Fetch and process workout data for dashboard
   */
  static async fetchWorkoutData(
    dataSource: string,
    apiKey?: string,
    _csvFilePath?: string
  ): Promise<{
    calendarData: ExtendedCalendarDay[]
    allExercises: CategorizedOneRepMax[]
    oneRepMaxes: CategorizedOneRepMax[]
    weeklyStats: WeeklyStats
    workouts: Workout[]
  }> {
    try {
      if (dataSource === 'api' && apiKey) {
        const workouts = await this.fetchAllWorkouts(apiKey)
        return DataProcessor.processWorkoutData(workouts)
      }

      throw new Error('Invalid data source configuration')
    } catch (error) {
      console.error('Failed to fetch workout data:', error)
      throw error
    }
  }

}
